import { queryOptions } from '@tanstack/react-query';

import { Fund } from '@/pages/setup-fund/utils/type';

import { EntityApprovalStatus } from '../entity/types';
import { apiInvestmentFetch, apiLookupFetch } from '../index';
import {
  FundApprovalStatus,
  FundDraftStatusType,
  InvestmentAvailabilityStatusType,
  MyFundMembership,
} from './types';

export const FUND_QUERY_KEYS = {
  funds: 'funds',
  files: 'files',
  fundsView: 'fundsView',
  userFollowedFunds: 'userFollowedFunds',
  coverImages: 'coverImages',
  navData: 'navData',
  exemptions: 'exemptions',
  fundCategories: 'fundCategories',
  fundAdminOptions: 'fundAdminOptions',
  auditorOptions: 'auditorOptions',
  fundInvitations: 'fundInvitations',
  fundMembers: 'fundMembers',
  fundsPublic: 'fundsPublic',
  fundsPublicById: 'fundsPublicById',
  fundMembershipsByUserId: 'fundMembershipsByUserId',
  fundMemberships: 'fundMemberships',
};

export type AllFund = {
  id: string;
  entity: {
    id: string;
    name: string;
    isEntityApproved: boolean;
    entityApprovalStatus?: EntityApprovalStatus | null;
  };
  name: string | null;
  displayName: string | null;
  logoUrl: string | null;
  coverImageUrl: string | null;
  cardImageUrl: string | null;
  entityLogoUrl: string | null;
  category: string | null;
  investmentAvailabilityStatus: InvestmentAvailabilityStatusType | null;
  draftStatus: FundDraftStatusType | null;
  sizeInMillionsUsd: number | null;
  aumInMillionsUsd: number | null;
  fundApprovalStatus: FundApprovalStatus;
  minInvestment: {
    currency: string;
    amount: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  investors: number;
  unitsIssued: number;
  isApproved: boolean;
  entityName: string;
  targetReturnRate: number | null;
  blockedAt?: string | null;
  blockReason?: string | null;
};
type GetFundsRes = {
  funds: AllFund[];
};

export const _getAllFundsOptions = ({
  userId,
  entityId,
}: {
  userId?: string;
  entityId?: string;
}) => {
  let queryKey = [FUND_QUERY_KEYS.funds] as string[];
  if (userId) queryKey = [...queryKey, userId];
  if (entityId) queryKey = [...queryKey, entityId];

  const searchParams = new URLSearchParams();
  if (userId) searchParams.append('userId', userId);
  if (entityId) searchParams.append('entityId', entityId);
  const url = '/funds' + (searchParams.toString() ? `?${searchParams.toString()}` : '');

  return queryOptions({
    queryKey,
    queryFn: () => apiInvestmentFetch<GetFundsRes>(url),
    refetchOnMount: true,
  });
};

///////////////////////////////////////////////

export type FundPublic = Omit<
  AllFund,
  | 'entity'
  | 'logoUrl'
  | 'draftStatus'
  | 'fundApprovalStatus'
  | 'createdAt'
  | 'updatedAt'
  | 'investors'
  | 'unitsIssued'
  | 'isApproved'
> & {
  slug: string;
  transferability: string | null;
};

export type GetFundsPublicRes = {
  funds: FundPublic[];
};

export const _getFundsPublic = () => {
  return queryOptions({
    queryKey: [FUND_QUERY_KEYS.fundsPublic],
    queryFn: () => apiInvestmentFetch<GetFundsPublicRes>('/funds/public'),
    refetchOnMount: true,
  });
};

///////////////////////////////////////////////

export const _getFundsPublicById = ({ identifier }: { identifier: string }) => {
  return queryOptions({
    enabled: !!identifier,
    queryKey: [FUND_QUERY_KEYS.fundsPublicById, identifier],
    queryFn: () => apiInvestmentFetch<GetPublicFundRes>(`/funds/public/${identifier}`),
    refetchOnMount: true,
    staleTime: 30 * 1000,
  });
};

///////////////////////////////////////////////

export type GetSingleFundRes = Fund & {
  isEntityApproved: boolean;
  entityApprovalStatus: EntityApprovalStatus | null;
  isApproved: boolean;
  fundApprovalStatus: FundApprovalStatus;
  coverImageUrl: string;
  logoUrl: string | null;
  blockReason?: string | null;
  blockInternalReference?: string | null;
};

export type GetPublicFundRes = GetSingleFundRes & { slug: string };

export const _getSingleFundOptions = (id?: string) => {
  return queryOptions({
    enabled: !!id,
    queryKey: [FUND_QUERY_KEYS.funds, id],
    queryFn: () => apiInvestmentFetch<GetSingleFundRes>(`/funds/${id}`),
    refetchOnMount: true,
  });
};

type GetCoverImagesRes = {
  fileId: string;
  type: string;
};

export const _getCoverImages = () => {
  return queryOptions({
    queryKey: [FUND_QUERY_KEYS.coverImages],
    queryFn: () => apiLookupFetch<GetCoverImagesRes[]>(`/images`),
    retry: 1,
  });
};

type GetExemptionsRes = {
  items: {
    id: string;
    country: string;
    exemptions: {
      id: string;
      exemptionType: string;
      criteria: string;
      maxInvestors: number;
    }[];
  }[];
};

export const _getExemptions = () => {
  return queryOptions({
    queryKey: [FUND_QUERY_KEYS.exemptions],
    queryFn: () => apiInvestmentFetch<GetExemptionsRes>(`/exemptions`),
    retry: 1,
  });
};

export type CategoryAsset = {
  code: string;
  name: string;
};

export type CategoryStrategy = {
  code: string;
  name: string;
};

export type CategoryStage = {
  code: string;
  name: string;
};

export type FundCategory = {
  code: string;
  name: string;
  assets: CategoryAsset[];
  strategies: CategoryStrategy[];
  stages: CategoryStage[];
};

export interface GetFundCategoriesRes {
  categories: FundCategory[];
}

export const _getFundCategories = () =>
  queryOptions({
    queryKey: [FUND_QUERY_KEYS.fundCategories],
    queryFn: () => apiInvestmentFetch<GetFundCategoriesRes>('/fund-categories'),
    refetchOnMount: true,
    retry: 1,
  });

///////////////////////////////////////////////
// Fund Admin Options (predefined dropdown reference list)

export type FundAdminOption = {
  name: string;
  logoImageId: string | null;
};

export const FUND_ADMIN_OTHER_OPTION = 'Other';

export const _getFundAdminOptions = () =>
  queryOptions({
    queryKey: [FUND_QUERY_KEYS.fundAdminOptions],
    queryFn: () => apiInvestmentFetch<FundAdminOption[]>('/fund-admin-options'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  });

///////////////////////////////////////////////
// Auditor Options (predefined dropdown reference list)

export type AuditorOption = {
  name: string;
  logoImageId: string | null;
  website: string | null;
};

export const AUDITOR_OTHER_OPTION = 'Other';

export const _getAuditorOptions = () =>
  queryOptions({
    queryKey: [FUND_QUERY_KEYS.auditorOptions],
    queryFn: () => apiInvestmentFetch<AuditorOption[]>('/auditor-options'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  });

///////////////////////////////////////////////

export const _fundsView = (id: string) => {
  return queryOptions({
    enabled: !!id,
    queryKey: [FUND_QUERY_KEYS.fundsView, id],
    queryFn: () =>
      apiInvestmentFetch(`/funds-views`, {
        method: 'POST',
        body: {
          fundId: id,
        },
      }),
    retry: 1,
    refetchOnMount: true,
  });
};

///////////////////////////////////////////////

export type FundsViewRes = {
  fundViews: {
    entityName: string;
    fundId: string;
    fundName: string;
    fundSize: number;
    viewedAt: string;
    coverImageId?: string;
    entityLogoUrl: string;
    slug: string;
  }[];
};

export const _getFundsView = () => {
  return queryOptions({
    queryKey: [FUND_QUERY_KEYS.fundsView],
    queryFn: () =>
      apiInvestmentFetch<FundsViewRes>(`/funds-views`, {
        method: 'GET',
      }),
    retry: 1,
    refetchOnMount: true,
  });
};

///////////////////////////////////////////////

export type UserFollowedFundsRes = {
  followedFunds: {
    fundId: string;
    fundName: string;
    coverImageId: string;
    entityName: string;
    fundSize: number;
    entityLogoUrl: string;
    slug: string;
  }[];
  limit: number;
  page: number;
  total: number;
};

export const _getUsersFollowedFunds = () => {
  return queryOptions({
    queryKey: [FUND_QUERY_KEYS.userFollowedFunds],
    queryFn: () =>
      apiInvestmentFetch<UserFollowedFundsRes>(
        `/fund-followers/user-followed-funds?page=1&limit=100`,
        {
          method: 'GET',
        },
      ),
    refetchOnMount: true,
    retry: 1,
  });
};

///////////////////////////////////////////////

export const _getCheckFundFollowed = (id: string) => {
  return queryOptions({
    enabled: !!id,
    queryKey: [FUND_QUERY_KEYS.userFollowedFunds, id],
    queryFn: () =>
      apiInvestmentFetch<{ isFollowing: boolean }>(`/fund-followers/check-status?fundId=${id}`),
    retry: 1,
    refetchOnMount: true,
  });
};

///////////////////////////////////////////////

export type NavDataEntry = {
  value: number;
  date: string;
};

export type NavDataMode = 'merge' | 'replace';

export type NavDataRes = {
  mode: NavDataMode;
  entries: NavDataEntry[];
};

///////////////////////////////////////////////

export type NavDataItem = {
  value: number;
  date: string;
};

export type NavDataItemRes = {
  items: NavDataItem[];
  count: number;
};

export const _getNetAssetsValues = ({
  id,
  fromDate,
  toDate,
}: {
  id: string;
  fromDate?: string;
  toDate?: string;
}) => {
  const hasRange = Boolean(fromDate && toDate);
  const params = hasRange ? { fromDate: fromDate!, toDate: toDate! } : undefined;
  return queryOptions({
    queryKey: [FUND_QUERY_KEYS.navData, id, fromDate ?? null, toDate ?? null],
    queryFn: () =>
      apiInvestmentFetch<NavDataItemRes>(`funds/${id}/net-assets-values`, {
        params,
      }),
    refetchOnMount: true,
    retry: 1,
  });
};

///////////////////////////////////////////////

export type FundInvitationsRes = {
  id: string;
  fundId: string;
  email: string;
  userId: string;
  status: string;
  invitedBy: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  userName: string;
};
export const _getFundInvitations = (id?: string) => {
  return queryOptions({
    queryKey: [FUND_QUERY_KEYS.fundInvitations, id],
    queryFn: () => apiInvestmentFetch<FundInvitationsRes[]>(`funds/${id}/member-invitations`),
    enabled: !!id,
    retry: 1,
  });
};

///////////////////////////////////////////////

export type FundMembersRes = {
  id: string;
  fundId: string;
  userId: string;
  // The endpoint returns membership only — identity is resolved per member
  // through the entity team lookup.
  name?: string;
  email?: string;
  title: string;
  approverId: string;
  approvedAt: string;
  createdAt: string;
  updatedAt: string;
};

export const _getFundMembers = (id?: string) => {
  return queryOptions({
    queryKey: [FUND_QUERY_KEYS.fundMembers, id],
    queryFn: () => apiInvestmentFetch<FundMembersRes[]>(`funds/${id}/members`),
    refetchOnMount: true,
    enabled: !!id,
    retry: 1,
  });
};

///////////////////////////////////////////////

export type FundMembershipsRes = {
  fundId: string;
  fundName: string;
  fundDescription: string;
  coverImageId: string;
  userTitle: string;
  activeSince: string;
  fundRole: string;
  entityId: string;
  entityName: string;
  fundApprovalStatus: FundApprovalStatus;
  entityApprovalStatus: EntityApprovalStatus;
};

export const getFundMembershipsByUserId = (id?: string) => {
  return queryOptions({
    queryKey: [FUND_QUERY_KEYS.fundMembershipsByUserId, id],
    queryFn: () =>
      apiInvestmentFetch<FundMembershipsRes[]>(`fund-memberships/users/${id}/memberships`),
    refetchOnMount: true,
    enabled: !!id,
    retry: 1,
  });
};

export const _getMyFundMemberships = () => {
  return queryOptions({
    queryKey: [FUND_QUERY_KEYS.fundMemberships, 'me'],
    queryFn: () => apiInvestmentFetch<MyFundMembership[]>('/fund-memberships/me'),
    refetchOnMount: true,
  });
};
